import { Util, DefaultTransitionDuration } from "@ts3d-hoops/web-viewer";
import type { Camera, IWebViewer, NodeId } from "@ts3d-hoops/web-viewer";

/** The prototype-built helper instance: every field this file assigns and every method bolted onto
 *  the prototype below. Exported because the three consumers each had to re-declare their own
 *  partial copy of it. */
export interface IsolateZoomHelperInstance {
  _camera: Camera | null;
  _deselectOnIsolate: boolean;
  _deselectOnZoom: boolean;
  _isolateStatus: boolean;
  _viewer: IWebViewer;
  _noteTextManager: IWebViewer["noteTextManager"];
  _setCamera(camera: Camera): void;
  setDeselectOnIsolate(deselect: boolean): void;
  getIsolateStatus(): boolean;
  isolateNodes(nodeIds: NodeId[], initiallyHiddenStayHidden?: boolean | null): Promise<void>;
  fitNodes(nodeIds: NodeId[]): Promise<void>;
  showAll(): Promise<void>;
  _updatePinVisibility(): Promise<void>;
}

/**
 * `IsolateZoomHelper` is a plain function declaration, so TypeScript gives it a call signature but
 * no construct signature — every `new IsolateZoomHelper(...)` is therefore an implicit `any`.
 * Converting it to a `class` would change the emitted JS, so consumers assert through this instead:
 * `new (IsolateZoomHelper as typeof IsolateZoomHelper & IsolateZoomHelperConstructor)(hwv)`.
 */
export type IsolateZoomHelperConstructor = new (viewer: IWebViewer) => IsolateZoomHelperInstance;

function _filterActiveSheetNodeIds(viewer: IWebViewer, nodeIds: NodeId[]) {
  const { model } = viewer;
  const activeSheetId = viewer.sheetManager.getActiveSheetId();
  if (activeSheetId !== null) {
    const sheetParent = model.getNodeParent(activeSheetId);
    // KNOWN BUG, preserved verbatim: `getNodeParent` returns `NodeId | null` and the null case is
    // not handled here. Erased assertion; a guard would change behaviour.
    const sheets_1 = model.getNodeChildren(sheetParent!);
    Util.filterInPlace(nodeIds, (id: NodeId) => {
      let parentId: NodeId | null = id;
      while (parentId !== null) {
        if (parentId === activeSheetId) {
          return true;
        }
        if (sheets_1.indexOf(parentId) !== -1) {
          return false;
        }
        parentId = viewer.model.getNodeParent(parentId);
      }
      return true;
    });
  }
}

export default function IsolateZoomHelper(this: IsolateZoomHelperInstance, viewer: IWebViewer) {
  const _this = this;
  this._camera = null;
  this._deselectOnIsolate = true;
  this._deselectOnZoom = true;
  this._isolateStatus = false;
  this._viewer = viewer;
  this._noteTextManager = this._viewer.noteTextManager;
  this._viewer.setCallbacks({
    modelSwitched() {
      _this._camera = null;
    },
  });
}
IsolateZoomHelper.prototype._setCamera = function (camera: Camera) {
  if (this._camera === null) {
    this._camera = camera;
  }
};
IsolateZoomHelper.prototype.setDeselectOnIsolate = function (deselect: boolean) {
  this._deselectOnIsolate = deselect;
};
IsolateZoomHelper.prototype.getIsolateStatus = function () {
  return this._isolateStatus;
};
IsolateZoomHelper.prototype.isolateNodes = function (nodeIds: NodeId[], initiallyHiddenStayHidden: boolean | null) {
  if (initiallyHiddenStayHidden === void 0) {
    initiallyHiddenStayHidden = null;
  }
  const { view } = this._viewer;
  this._setCamera(view.getCamera());
  _filterActiveSheetNodeIds(this._viewer, nodeIds);
  const p = view.isolateNodes(
    nodeIds,
    DefaultTransitionDuration,
    !this._viewer.sheetManager.isDrawingSheetActive(),
    initiallyHiddenStayHidden,
  );
  // if (this._deselectOnIsolate) {
  //   this._viewer.selectionManager.clear();
  // }
  this._isolateStatus = true;
  return p;
};
IsolateZoomHelper.prototype.fitNodes = function (nodeIds: NodeId[]) {
  const { view } = this._viewer;
  this._setCamera(view.getCamera());
  const p = view.fitNodes(nodeIds);
  // if (this._deselectOnZoom) {
  //   this._viewer.selectionManager.clear();
  // }
  return p;
};
IsolateZoomHelper.prototype.showAll = function () {
  const { model } = this._viewer;
  if (this._viewer.sheetManager.isDrawingSheetActive()) {
    const sheetId = this._viewer.sheetManager.getActiveSheetId();
    if (sheetId !== null) {
      return this.isolateNodes([sheetId]);
    }
    return Promise.resolve();
  }

  const ps: Promise<void>[] = [];
  if (model.isDrawing()) {
    // Need to reset 3DNodes here
    const nodes3D = this._viewer.sheetManager.get3DNodes();
    ps.push(this.isolateNodes(nodes3D));
  } else ps.push(model.resetNodesVisibility());
  if (this._camera !== null) {
    this._viewer.view.setCamera(this._camera, DefaultTransitionDuration);
    this._camera = null;
  }
  this._isolateStatus = false;
  ps.push(this._updatePinVisibility());
  return Util.waitForAll(ps);
};
IsolateZoomHelper.prototype._updatePinVisibility = function () {
  this._noteTextManager.setIsolateActive(this._isolateStatus);
  return this._noteTextManager.updatePinVisibility();
};
